import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DebugLabel } from '../components/shared';
import { EnhancedNavigation } from '../components/navigation/EnhancedNavigation';
import { MobileBottomNav } from '../components/navigation/MobileBottomNav';
import { AnnouncementBanner } from '../components/AnnouncementBanner';
import { TokenQuotaWarningBanner } from '../components/shared/TokenQuotaWarningBanner';
import { useAuth } from '../contexts/AuthContext';
import { FeedbackModalProvider } from '../contexts/FeedbackModalContext';
import { PageControlsProvider } from '../contexts/PageControlsContext';
import { useDevice } from '../features/consensus/hooks/useDevice';
import { isFeatureEnabled } from '../utils/featureFlags';
import { entitlementService } from '../services/entitlement';

interface MainLayoutProps {
  children: React.ReactNode;
  showBreadcrumb?: boolean;
  breadcrumbText?: string;
}

export const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  showBreadcrumb = false,
  breadcrumbText
}) => {
  const navigate = useNavigate();
  const { user: firebaseUser, dbProfile, signOut } = useAuth();
  const { isMobile } = useDevice();
  const [userPlan, setUserPlan] = React.useState<'free' | 'pro'>('free');
  const [showTokenWarningBanner, setShowTokenWarningBanner] = React.useState(false);
  const [tokenQuotaData, setTokenQuotaData] = React.useState<{ used: number; total: number; percentage: number; planName: string; upgradeUrl?: string } | null>(null);

  // NEW_DESIGN_BETA graduated - features always enabled
  const feedbackModalEnabled = !!firebaseUser;

  // Fetch user's plan from entitlement service
  const [planDisplayName, setPlanDisplayName] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (!firebaseUser) {
      setUserPlan('free');
      setPlanDisplayName(undefined);
      return;
    }

    // Check cache first (synchronous) to avoid flash
    const cachedStatus = entitlementService.getCachedStatusSync();
    if (cachedStatus) {
      // If paywall is disabled in cache, use unlimited defaults
      if (!cachedStatus.paywallEnabled) {
        setUserPlan('pro');
        setPlanDisplayName('Nexus');
        console.log('[MainLayout] Using cached status with paywall disabled - setting unlimited defaults');
        return;
      }

      const isPro = cachedStatus.plan?.code === 'flow' || cachedStatus.plan?.code === 'nexus';
      setUserPlan(isPro ? 'pro' : 'free');
      setPlanDisplayName(cachedStatus.plan?.displayName);
      console.log('[MainLayout] Using cached plan:', cachedStatus.plan?.displayName);
    }

    const fetchPlan = async () => {
      try {
        const status = await entitlementService.getStatus();

        // If paywall is disabled, set unlimited defaults and skip further processing
        if (!status.paywallEnabled) {
          setUserPlan('pro');
          setPlanDisplayName('Nexus');
          console.log('[MainLayout] Paywall disabled - using unlimited defaults');
          return;
        }

        // Map plan codes to simple free/pro for nav display
        // edge = free tier, flow/nexus = pro tier
        const isPro = status.plan?.code === 'flow' || status.plan?.code === 'nexus';
        setUserPlan(isPro ? 'pro' : 'free');

        // Use the actual plan display name from the API
        setPlanDisplayName(status.plan?.displayName ? `${status.plan.displayName}` : undefined);

        // Check token quota status for warning banner (show when >= 80%)
        if (status.quotas?.tokens) {
          const tokenQuota = status.quotas.tokens;
          const tokenBannerDismissed = sessionStorage.getItem('tokenBannerDismissed') === 'true';

          if (tokenQuota.percentage >= 80 && !tokenBannerDismissed) {
            setShowTokenWarningBanner(true);
            setTokenQuotaData({
              used: tokenQuota.used,
              total: tokenQuota.limit,
              percentage: tokenQuota.percentage,
              planName: status.plan?.displayName || 'Edge',
              upgradeUrl: status.upgradeUrl
            });
          }
        }

        console.log('[MainLayout] Fetched fresh plan:', {
          planCode: status.plan?.code,
          displayName: status.plan?.displayName,
          mappedTo: isPro ? 'pro' : 'free',
        });
      } catch (error) {
        console.error('[MainLayout] Error fetching plan:', error);
        // Fail-open: use unlimited defaults on error
        setUserPlan('pro');
        setPlanDisplayName('Nexus');
        console.log('[MainLayout] Using unlimited defaults due to error');
      }
    };

    fetchPlan();
  }, [firebaseUser]);

  // Transform user data for EnhancedNavigation
  // Priority: DB profile (with Vercel or Google image) > Firebase fallback
  const user = firebaseUser ? {
    name: dbProfile?.name || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
    email: dbProfile?.email || firebaseUser.email || '',
    avatar: dbProfile?.image || firebaseUser.photoURL || undefined, // DB is source of truth, fallback to Firebase
    plan: userPlan,
    planDisplayName,
  } : null;

  // Debug logging for profile image
  React.useEffect(() => {
    console.log('🔍 MainLayout - Profile Image Debug:', {
      hasFirebaseUser: !!firebaseUser,
      hasDbProfile: !!dbProfile,
      dbProfileImage: dbProfile?.image,
      userAvatar: user?.avatar,
      firebasePhotoURL: firebaseUser?.photoURL,
    });
  }, [firebaseUser, dbProfile, user?.avatar]);

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <PageControlsProvider>
      <FeedbackModalProvider enabled={feedbackModalEnabled}>
        <div className="min-h-screen bg-transparent relative">
          <DebugLabel label="MainLayout-Container" type="layout" position="top-left" />
          {/* Enhanced Navigation - Fixed to top, always accessible above modals */}
          <div className="fixed top-0 left-0 right-0 z-[9999]">
          <EnhancedNavigation
              user={user}
              onSignIn={handleSignIn}
              onSignOut={handleSignOut}
            />

            {/* Announcement Banner */}
            <AnnouncementBanner />

            {/* Token Quota Warning Banner */}
            {showTokenWarningBanner && tokenQuotaData && (
              <TokenQuotaWarningBanner
                used={tokenQuotaData.used}
                total={tokenQuotaData.total}
                percentage={tokenQuotaData.percentage}
                planName={tokenQuotaData.planName}
                upgradeUrl={tokenQuotaData.upgradeUrl}
                onDismiss={() => {
                  setShowTokenWarningBanner(false);
                  sessionStorage.setItem('tokenBannerDismissed', 'true');
                }}
              />
            )}
          </div>

        {/* Breadcrumb */}
        {showBreadcrumb && breadcrumbText && (
          <div className="bg-slate-800/30 border-b border-slate-700/30">
            <div className="max-w-6xl mx-auto px-6 py-2">
              <span className="text-sm text-slate-400">{breadcrumbText}</span>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="relative pb-20 md:pb-0" style={{ paddingTop: 'var(--main-nav-height, 6rem)' }}>
          <DebugLabel label="MainLayout-MainContent" type="layout" position="bottom-left" />
          {children}
        </main>

        {/* Mobile Bottom Navigation */}
        <MobileBottomNav user={user} />
      </div>
    </FeedbackModalProvider>
    </PageControlsProvider>
  );
};
